import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPut } from '../../lib/api';
import { toast } from 'sonner';
import { Save, ArrowLeft, Building2, Loader2 } from 'lucide-react';

const FIELDS = [
  { group: 'Identity', items: [
    { key: 'company_name', label: 'Company Name' },
    { key: 'legal_name', label: 'Legal Name' },
    { key: 'gst_no', label: 'GST Number' },
    { key: 'pan', label: 'PAN' },
    { key: 'cin', label: 'CIN' },
    { key: 'logo_url', label: 'Logo URL' },
  ]},
  { group: 'Address', items: [
    { key: 'address_line1', label: 'Address Line 1' },
    { key: 'address_line2', label: 'Address Line 2' },
    { key: 'city', label: 'City' },
    { key: 'state', label: 'State' },
    { key: 'pincode', label: 'Pincode' },
    { key: 'country', label: 'Country' },
  ]},
  { group: 'Contact', items: [
    { key: 'phone', label: 'Phone' },
    { key: 'email', label: 'Email' },
    { key: 'website', label: 'Website' },
  ]},
  { group: 'Bank Details', items: [
    { key: 'bank_name', label: 'Bank Name' },
    { key: 'bank_account', label: 'Account Number' },
    { key: 'ifsc', label: 'IFSC' },
    { key: 'branch', label: 'Branch' },
  ]},
  { group: 'Signatory', items: [
    { key: 'signatory_name', label: 'Authorized Signatory Name' },
    { key: 'signatory_designation', label: 'Designation' },
  ]},
];

// Printed on every PO / MPR / CS header and Billing Address.
const DOC_REQUIRED = new Set(['address_line1', 'city', 'state', 'pincode', 'gst_no', 'pan']);
const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const PAN_RE = /^[A-Z]{5}[0-9]{4}[A-Z]$/;

const validateIds = (d) => {
  const errs = {};
  const gst = (d.gst_no || '').trim().toUpperCase();
  const pan = (d.pan || '').trim().toUpperCase();
  if (gst && !GSTIN_RE.test(gst)) errs.gst_no = 'GSTIN must be 15 characters: state code + PAN + entity no. + Z + check digit';
  if (pan && !PAN_RE.test(pan)) errs.pan = 'PAN must be in the format AAAAA9999A';
  if (gst && pan && !errs.gst_no && !errs.pan && gst.slice(2, 12) !== pan) errs.pan = `PAN does not match characters 3–12 of the GSTIN (${gst.slice(2, 12)})`;
  return errs;
};

export default function CompanyProfile() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiGet('/settings/company-profile').then((d) => { setData(d); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const errors = data ? validateIds(data) : {};
  const missing = data ? [...DOC_REQUIRED].filter(k => !String(data[k] || '').trim()) : [];

  const save = async () => {
    if (Object.keys(errors).length) { toast.error(Object.values(errors)[0]); return; }
    setSaving(true);
    try {
      await apiPut('/settings/company-profile', data);
      toast.success('Company profile saved');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  if (loading) return <div className="p-8 flex items-center gap-2 text-slate-500"><Loader2 size={14} className="animate-spin" /> Loading…</div>;

  return (
    <div className="p-6 md:p-8" data-testid="company-profile-page">
      <button onClick={() => navigate('/app/settings')} className="text-xs font-display font-bold uppercase tracking-wider text-slate-500 hover:text-[#3b4872] flex items-center gap-1 mb-3"><ArrowLeft size={12} /> Back to Settings</button>
      <PageHeader overline="Settings · Company Profile" title="Company Profile" subtitle="Logo, GST, PAN, address, bank and signatory details — used across PO, Invoice and RA Bill print outs." icon={Building2} />

      {missing.length > 0 && (
        <div className="mt-6 px-4 py-3 bg-amber-50 border border-amber-300 text-amber-800 text-sm rounded-sm" data-testid="cp-required-banner">
          Required for documents (PO / MPR / CS): Address line 1, City, State, Pincode, GSTIN and PAN. Missing: {missing.length} field(s).
        </div>
      )}
      <div className="space-y-5 mt-6">
        {FIELDS.map((g) => (
          <div key={g.group} className="bg-white border border-slate-200 rounded-sm p-5">
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.16em] text-[#3b4872] mb-3">{g.group}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {g.items.map((f) => (
                <label key={f.key} className="block">
                  <span className="text-xs font-semibold text-slate-600 block mb-1">
                    {f.label}{DOC_REQUIRED.has(f.key) && <span className="text-red-500 ml-0.5" title="Required for documents">*</span>}
                  </span>
                  <input
                    type="text"
                    value={data[f.key] || ''}
                    onChange={(e) => update(f.key, e.target.value)}
                    className={`w-full border rounded-sm px-3 py-2 text-sm focus:border-[#3b4872] focus:outline-none ${errors[f.key] ? 'border-red-400' : 'border-slate-300'}`}
                    data-testid={`cp-${f.key}`}
                  />
                  {errors[f.key] && <span className="text-[11px] text-red-600 mt-1 block" data-testid={`cp-${f.key}-error`}>{errors[f.key]}</span>}
                </label>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 flex justify-end sticky bottom-4">
        <button data-testid="cp-save-btn" onClick={save} disabled={saving} className="flex items-center gap-2 px-4 py-2.5 bg-[#3b4872] text-white text-sm rounded-sm font-display font-semibold hover:bg-[#2c3658] disabled:opacity-50 shadow-lg">
          <Save size={14} /> {saving ? 'Saving…' : 'Save Profile'}
        </button>
      </div>
    </div>
  );
}
